import React, { createContext, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import isCdsFrequentPassengerEnabled from 'utils/isCdsFrequentPassengerEnabled';
import {
  cdsFrequentPassengerActivation,
  cdsFrequentPassengerEnrollment,
  cdsFrequentPassengerLogin,
  cdsFrequentPassengerLogout,
  clearCdsFpError,
  fetchCdsFrequentPassengerAccountStatus,
  resetCdsFpEnrollmentFlow,
} from '@/actions/cdsFrequentPassenger';

const CdsFrequentPassengerContext = createContext(null);

/**
 * Provider for CDS Pasajero Frecuente loyalty session state.
 * Bootstraps account status on load when the feature flag is enabled.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function CdsFrequentPassengerProvider({ children }) {
  const dispatch = useDispatch();
  const { brand } = useWhitelabelEnvs();
  const features = useSelector((state) => state.whitelabelConfig?.features ?? {});
  const isAuthenticated = useSelector((state) => state.cdsFrequentPassenger.get('isAuthenticated'));
  const isLoading = useSelector((state) => state.cdsFrequentPassenger.get('isLoading'));
  const isBootstrapped = useSelector((state) => state.cdsFrequentPassenger.get('isBootstrapped'));
  const error = useSelector((state) => state.cdsFrequentPassenger.get('error'));
  const accountStatus = useSelector((state) => state.cdsFrequentPassenger.get('accountStatus'));
  const enrollmentStep = useSelector((state) => state.cdsFrequentPassenger.get('enrollmentStep'));
  const enrollmentIdentity = useSelector((state) => {
    return state.cdsFrequentPassenger.get('enrollmentIdentity');
  });
  const activationResult = useSelector((state) => {
    return state.cdsFrequentPassenger.get('activationResult');
  });
  const programToken = useSelector((state) => state.cdsFrequentPassenger.get('programToken'));

  const enabled = isCdsFrequentPassengerEnabled(features, brand);
  const hasBootstrapped = useRef(false);

  useEffect(() => {
    if (!enabled || hasBootstrapped.current) return;

    hasBootstrapped.current = true;
    // Silent bootstrap: failures (401, network errors) are already surfaced via the
    // `error` Redux state below. The action re-throws for explicit callers (login,
    // activation) to react to, but this background dispatch has no caller to catch
    // it — swallow here so a network failure doesn't reach Sentry as an unhandled
    // rejection.
    dispatch(fetchCdsFrequentPassengerAccountStatus({ silentUnauthorized: true })).catch(() => {});
  }, [dispatch, enabled]);

  const value = {
    enabled,
    isAuthenticated,
    isLoading,
    isBootstrapped,
    error,
    accountStatus,
    enrollmentStep,
    enrollmentIdentity,
    activationResult,
    programToken,
    login: (payload) => dispatch(cdsFrequentPassengerLogin(payload)),
    logout: () => dispatch(cdsFrequentPassengerLogout()),
    fetchAccountStatus: () => dispatch(fetchCdsFrequentPassengerAccountStatus()),
    enrollment: (payload) => dispatch(cdsFrequentPassengerEnrollment(payload)),
    activation: (payload) => dispatch(cdsFrequentPassengerActivation(payload)),
    clearError: () => dispatch(clearCdsFpError()),
    resetEnrollmentFlow: () => dispatch(resetCdsFpEnrollmentFlow()),
  };

  return (
    <CdsFrequentPassengerContext.Provider value={value}>
      {children}
    </CdsFrequentPassengerContext.Provider>
  );
}

CdsFrequentPassengerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook for CDS Pasajero Frecuente loyalty session.
 * @returns {object}
 */
export function useCdsFrequentPassenger() {
  const context = useContext(CdsFrequentPassengerContext);

  if (!context) {
    throw new Error('useCdsFrequentPassenger must be used within a CdsFrequentPassengerProvider');
  }

  return context;
}
